import {
  STORES,
  getAll,
  get,
  put,
  deleteRecord,
  getByIndex,
} from '@utils/indexedDB'
import type { RecordingDraft, Trail, TrailAttempt, TrailPhoto } from '../types'
import { MIN_RANKING_COVERAGE } from '../types'

/** Fixed key for the single in-progress recording checkpoint. */
const DRAFT_KEY = 'current' as const

/**
 * Local-only persistence for recorded trails and their waypoint photos.
 * Everything lives in IndexedDB (HiddenAdventuresDB, see utils/indexedDB.ts);
 * nothing here ever reaches the server. Photos are kept in a sibling store so
 * listing trails doesn't pull image bytes.
 */

/** All trails, most-recently-updated first. */
export async function listTrails(): Promise<Trail[]> {
  const trails = await getAll<Trail>(STORES.TRAILS)
  return trails.sort((a, b) => b.updatedAt - a.updatedAt)
}

export function getTrail(id: string): Promise<Trail | undefined> {
  return get<Trail>(STORES.TRAILS, id)
}

/** Insert or update a trail, stamping updatedAt. */
export function saveTrail(trail: Trail): Promise<void> {
  return put(STORES.TRAILS, { ...trail, updatedAt: Date.now() })
}

/** Delete a trail and cascade-delete all of its photos and attempts. */
export async function deleteTrail(id: string): Promise<void> {
  const photos = await getByIndex<TrailPhoto>(STORES.TRAIL_PHOTOS, 'trailId', id)
  await Promise.all(photos.map((p) => deleteRecord(STORES.TRAIL_PHOTOS, p.id)))
  const attempts = await getByIndex<TrailAttempt>(STORES.TRAIL_ATTEMPTS, 'trailId', id)
  await Promise.all(attempts.map((a) => deleteRecord(STORES.TRAIL_ATTEMPTS, a.id)))
  await deleteRecord(STORES.TRAILS, id)
}

// ---- Attempts: timed walks of a trail ----

/**
 * Save a timed walk. Keyed by the attempt's own id, which is minted when the walk
 * starts, so writing the same attempt twice overwrites rather than duplicating.
 */
export function saveAttempt(attempt: TrailAttempt): Promise<void> {
  return put(STORES.TRAIL_ATTEMPTS, attempt)
}

/** A trail's attempts, fastest first. */
export async function listAttempts(trailId: string): Promise<TrailAttempt[]> {
  const attempts = await getByIndex<TrailAttempt>(STORES.TRAIL_ATTEMPTS, 'trailId', trailId)
  return attempts.sort((a, b) => rankingMs(a) - rankingMs(b))
}

export function deleteAttempt(id: string): Promise<void> {
  return deleteRecord(STORES.TRAIL_ATTEMPTS, id)
}

/** The time an attempt is judged on: moving time when measured, else wall clock. */
export function rankingMs(a: TrailAttempt): number {
  return a.movingMs ?? a.elapsedMs
}

/**
 * The best time on a trail, or undefined until somebody has walked enough of it.
 * Short walks are kept but never rank -- see TrailAttempt.coveragePercent.
 */
export function personalBest(attempts: TrailAttempt[]): TrailAttempt | undefined {
  return attempts
    .filter((a) => a.coveragePercent >= MIN_RANKING_COVERAGE)
    .sort((a, b) => rankingMs(a) - rankingMs(b))[0]
}

export function savePhoto(photo: TrailPhoto): Promise<void> {
  return put(STORES.TRAIL_PHOTOS, photo)
}

export function getPhoto(id: string): Promise<TrailPhoto | undefined> {
  return get<TrailPhoto>(STORES.TRAIL_PHOTOS, id)
}

export function deletePhoto(id: string): Promise<void> {
  return deleteRecord(STORES.TRAIL_PHOTOS, id)
}

export function getPhotosForTrail(trailId: string): Promise<TrailPhoto[]> {
  return getByIndex<TrailPhoto>(STORES.TRAIL_PHOTOS, 'trailId', trailId)
}

/**
 * In-progress recording checkpoint. There is only ever one draft (fixed key),
 * overwritten in place every few seconds while recording. The recorder hook
 * writes it; TrailRecorderPage reads it on mount to offer a resume prompt.
 */
export function saveRecordingDraft(draft: RecordingDraft): Promise<void> {
  return put(STORES.RECORDING_DRAFT, { ...draft, id: DRAFT_KEY, updatedAt: Date.now() })
}

export function getRecordingDraft(): Promise<RecordingDraft | undefined> {
  return get<RecordingDraft>(STORES.RECORDING_DRAFT, DRAFT_KEY)
}

export function clearRecordingDraft(): Promise<void> {
  return deleteRecord(STORES.RECORDING_DRAFT, DRAFT_KEY)
}
